// Pure derivation from the radiopi /analyze response (see
// track-analysis-client.js for the actual network call) - plain data in,
// plain data out, unit-testable without a browser, same convention as
// orchestrator.js.
import { splitBeatCount, BEATS_PER_BAR, BEATS_PER_PHRASE, BEATS_PER_SECTION, ACCENTS } from './beat-grid.js';

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

// Le BPM mesuré, s'il y en a un — la seule dérivation qui survit hors de la
// timeline (player-boot s'en sert pour la barre). L'énergie par segment est
// continue et vit dans buildAnalysisTimeline().lookupEnergy.
export function bpmFromAnalysis(analysis) {
  return analysis && analysis.tempo && typeof analysis.tempo.bpm === 'number' ? analysis.tempo.bpm : null;
}

// --- Phase 2: mark selection by non-maxima suppression ---

// Most of the API's own "drops" array is really onset-strength candidates,
// not drops in the DJ sense - a real 353s track returned 77 of them, the
// vast majority under 0.35 confidence. A single fixed bar doesn't fit
// every track (a sparse track has none above 0.7; a drop-heavy track
// saturates well past it) - the 90th percentile of THIS track's own drop
// confidences, bounded, adapts to either case.
const DROP_PERCENTILE = 0.90;
const DROP_MIN_CONFIDENCE_FLOOR = 0.55;
const DROP_MIN_CONFIDENCE_CEIL = 0.85;

// A segment-to-segment avg_db swing of this many dB or more counts as a
// "full strength" rise/fall candidate (clamped 0..1 below this).
const DB_SWING_SCALE = 12;
// A fall is structurally real but reads as less spectacular than a rise -
// same swing, lower score.
const FALL_SCORE_MULT = 0.8;
const MIX_BOUNDARY_SCORE = 0.9;

// Two candidates within this many ms are the same moment - collapsed to
// one (max score, that mark's own type) so a drop landing right on a
// segment's own level transition doesn't fire two swaps back to back.
const MERGE_WINDOW_MS = 500;

// Non-maxima suppression gap: an ALREADY-ACCEPTED mark blocks any other
// candidate within this many ms of it, regardless of arrival order - the
// old (pre-rewrite) selection kept the first mark of each window it saw,
// which could and did drop a confidence-1.0 drop in favor of a weaker
// transition that simply came first in time. Sorting by score before
// applying the gap fixes that: the strongest events win their slot.
const MIN_GAP_MS = 8000;

// PAS DE PLAFOND au nombre de marks. Il y en avait un — « environ un toutes
// les 22 s », entre quatre et quatorze — et il écartait des points de
// mixage réels : sur un morceau à douze ruptures franches, deux passaient
// sans que l'image bouge. Ce qui borne désormais, c'est la SÉLECTION en
// amont (percentile de confiance des drops, scores des montées et
// descentes) et l'écart minimal MIN_GAP_MS entre deux marks retenus.
// `targetCountOverride` reste, pour les tests.

function percentileOf(sortedAsc, p) {
  if (sortedAsc.length === 0) return 0;
  const idx = clamp(Math.round(p * (sortedAsc.length - 1)), 0, sortedAsc.length - 1);
  return sortedAsc[idx];
}

// Sorted, force-selected marks: [{atMs, type, score}], atMs ascending.
// type is one of 'drop' | 'rise' | 'fall' | 'mix_in' | 'mix_out'. Replaces
// the old forcedSwapTimesMs (plain ms array, single implicit type,
// arrival-order selection) - callers that need the old flat timestamp
// list can map(m => m.atMs) themselves.
// opts.dropConfidenceMinOverride / minGapMsOverride / targetCountOverride
// override the adaptive defaults, for testing.
// LES PHRASES D'ABORD (moteur 1.1.0, phrases.py) : le service a déjà choisi
// les points de mixage — nouveauté sur la grille des temps, a priori de
// phrase — et c'est la seule sélection. Un point très neuf (≥ 3 × la
// médiane) devient un `drop` fort : coupe FLASH immédiate ; les autres sont
// des `phrase` : coupe CUT calée sur le temps. Ni segments ni drops ni
// bornes de mix n'y sont ajoutés : sur le jeu de référence, cette règle-là
// inventait plus de points qu'elle n'en trouvait. Les analyses d'avant
// (sans `phrases`) gardent l'ancienne règle, ci-dessous.
const PHRASE_DROP_NOVELTY = 3.0;
const PHRASE_SCORE_SCALE = 4.0;
// UN DROP EST UNE ARRIVÉE, PAS UNE FRONTIÈRE FORTE (27/09/2026).
//
// La nouveauté mesure à quel point la phrase qui commence diffère de celle
// qui finit — elle est aussi haute quand la musique TOMBE dans un passage
// calme que quand elle explose. Sur « Freed From Desire », onze points sur
// dix-sept dépassaient 3,0 et étaient tous des drops : le compte à rebours
// s'est lancé vers 3:14, qui est le début du breakdown, alors que les vrais
// drops sont à 1:14 et 3:29. Un drop, c'est la phrase HAUTE qui arrive après
// au moins DROP_BUILD_MIN_MS sans phrase haute (une montée, une intro, un
// breakdown — pas une respiration de sept secondes au milieu du refrain),
// avec un saut d'énergie d'au moins DROP_RISE_MIN. Quand le creux est
// descendu jusqu'au niveau BAS (une coupure, un silence — « Call on Me »
// à 1:22, sept secondes de rien avant le drop de 1:29), DROP_BREAK_MIN_MS
// suffit : ce n'est plus une respiration, c'est une cassure. Les points
// d'avant le niveau (moteur 1.1.0) gardent la règle sur la seule nouveauté.
const DROP_BUILD_MIN_MS = 12000;
const DROP_BREAK_MIN_MS = 6000;
const DROP_RISE_MIN = 25;

function marksFromPhrases(phrases) {
  const out = [];
  const points = phrases.points.slice().filter(function (p) { return p && typeof p.time_ms === 'number'; })
    .sort(function (a, b) { return a.time_ms - b.time_ms; });
  const avecNiveau = points.some(function (p) { return p.level === 'low' || p.level === 'mid' || p.level === 'high'; });
  const sections = Array.isArray(phrases.sections) ? phrases.sections : [];
  // Depuis quand n'y a-t-il plus eu de phrase haute ? null = on y est.
  let sansHautDepuisMs = sections.length && sections[0].level === 'high' ? null : 0;
  let creuxBas = sections.length ? sections[0].level === 'low' : false;
  let energiePrecedente = null;
  for (const p of points) {
    const nov = typeof p.novelty === 'number' ? p.novelty : 1;
    const niveau = (p.level === 'low' || p.level === 'mid' || p.level === 'high') ? p.level : null;
    const energie = typeof p.energy === 'number' ? p.energy : null;
    let drop;
    if (!avecNiveau) {
      drop = nov >= PHRASE_DROP_NOVELTY;
    } else {
      const montee = energiePrecedente == null || energie == null || energie - energiePrecedente >= DROP_RISE_MIN;
      const creuxMs = sansHautDepuisMs != null ? p.time_ms - sansHautDepuisMs : -1;
      drop = nov >= PHRASE_DROP_NOVELTY && niveau === 'high' && montee
        && creuxMs >= (creuxBas ? DROP_BREAK_MIN_MS : DROP_BUILD_MIN_MS);
      if (niveau === 'high') { sansHautDepuisMs = null; creuxBas = false; }
      else if (niveau) {
        if (sansHautDepuisMs == null) sansHautDepuisMs = p.time_ms;
        if (niveau === 'low') creuxBas = true;
      }
      if (energie != null) energiePrecedente = energie;
    }
    // L'énergie et le niveau de la PHRASE QUI COMMENCE au point (moteur
    // 1.1.1) : l'orchestration s'en sert pour choisir dans la bande — voir
    // adaptive-party.js, _handleMark.
    const m = drop
      ? { atMs: p.time_ms, type: 'drop', score: clamp(0.85 + (nov - PHRASE_DROP_NOVELTY) * 0.05, 0.85, 1), novelty: nov }
      : { atMs: p.time_ms, type: 'phrase', score: clamp01(nov / PHRASE_SCORE_SCALE), novelty: nov };
    if (niveau) m.level = niveau;
    if (energie != null) m.energy = energie;
    out.push(m);
  }
  return out;
}

export function selectMarks(analysis, opts) {
  const phrases = analysis && analysis.structure && analysis.structure.phrases;
  if (phrases && Array.isArray(phrases.points)) return marksFromPhrases(phrases);
  const segments = analysis && analysis.structure && analysis.structure.segments;
  const drops = analysis && analysis.structure && analysis.structure.cue_points
    && analysis.structure.cue_points.drops;
  const durationS = analysis && analysis.duration_s;

  let dropMinConfidence = DROP_MIN_CONFIDENCE_CEIL;
  if (drops && drops.length > 0) {
    const confs = drops.map(function (d) { return d.confidence; }).sort(function (a, b) { return a - b; });
    dropMinConfidence = clamp(percentileOf(confs, DROP_PERCENTILE), DROP_MIN_CONFIDENCE_FLOOR, DROP_MIN_CONFIDENCE_CEIL);
  }
  if (opts && opts.dropConfidenceMinOverride != null) dropMinConfidence = opts.dropConfidenceMinOverride;

  const candidates = [];
  if (drops) {
    for (let i = 0; i < drops.length; i++) {
      if (drops[i].confidence >= dropMinConfidence) {
        candidates.push({ atMs: drops[i].time_ms, type: 'drop', score: drops[i].confidence });
      }
    }
  }
  if (segments) {
    for (let i = 1; i < segments.length; i++) {
      const prev = segments[i - 1], next = segments[i];
      const swing = next.avg_db - prev.avg_db;
      if (swing > 0) {
        candidates.push({ atMs: next.start_ms, type: 'rise', score: clamp01(swing / DB_SWING_SCALE) });
      } else if (swing < 0) {
        candidates.push({ atMs: next.start_ms, type: 'fall', score: clamp01(-swing / DB_SWING_SCALE) * FALL_SCORE_MULT });
      }
    }
  }
  const cue = analysis && analysis.structure && analysis.structure.cue_points;
  if (cue) {
    if (cue.mix_in_ms != null) candidates.push({ atMs: cue.mix_in_ms, type: 'mix_in', score: MIX_BOUNDARY_SCORE });
    if (cue.mix_out_ms != null) candidates.push({ atMs: cue.mix_out_ms, type: 'mix_out', score: MIX_BOUNDARY_SCORE });
  }

  candidates.sort(function (a, b) { return a.atMs - b.atMs; });
  const merged = [];
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    const last = merged[merged.length - 1];
    if (last && c.atMs - last.atMs <= MERGE_WINDOW_MS) {
      if (c.score > last.score) { last.score = c.score; last.type = c.type; }
    } else {
      merged.push({ atMs: c.atMs, type: c.type, score: c.score });
    }
  }

  const minGapMs = (opts && opts.minGapMsOverride != null) ? opts.minGapMsOverride : MIN_GAP_MS;
  const targetCount = (opts && opts.targetCountOverride != null) ? opts.targetCountOverride : Infinity;

  const byScoreDesc = merged.slice().sort(function (a, b) { return b.score - a.score; });
  const accepted = [];
  for (let i = 0; i < byScoreDesc.length && accepted.length < targetCount; i++) {
    const cand = byScoreDesc[i];
    let tooClose = false;
    for (let j = 0; j < accepted.length; j++) {
      if (Math.abs(accepted[j].atMs - cand.atMs) < minGapMs) { tooClose = true; break; }
    }
    if (!tooClose) accepted.push(cand);
  }
  accepted.sort(function (a, b) { return a.atMs - b.atMs; });
  return accepted;
}

// COUNTDOWN : UNE FOIS PAR MORCEAU, SUR LE DROP LE PLUS INTENSE, 12 TEMPS AU
// PLUS (27/09/2026, demande utilisateur ; en temps et non en secondes depuis
// le 29/09/2026). Parmi les drops de score >=
// COUNTDOWN_DROP_SCORE, un seul est visé : celui dont l'énergie juste après
// (lookupEnergy à +1,5 s, le « poids » du drop) est la plus haute ; à égalité
// le mieux noté, puis le premier. Sans énergie mesurée, le score départage.
// countdownTarget() le rend ; adaptive-party.js ne lance Countdown que vers
// lui, et une seule fois par morceau.
//
// LE POINT DE MIX. Si ce drop est identifié avec beaucoup de certitude
// (score >= COUNTDOWN_CERTAIN_SCORE), il reçoit son propre point de mix, type
// 'countdown', un nombre ENTIER de mesures avant lui - au plus
// COUNTDOWN_MAX_BEATS temps - pour que le compte parte sur un temps fort et
// tombe à zéro sur le drop. Il ne part pas avant la fin du drop précédent (une
// mesure après lui) et ne compte pas moins de COUNTDOWN_MIN_BEATS temps. Les autres
// points qui tombaient dans sa fenêtre sont retirés : Countdown tient l'écran
// jusqu'au drop, le panneau technique doit montrer ce que l'image fera.
export const COUNTDOWN_DROP_SCORE = 0.85;
export const COUNTDOWN_CERTAIN_SCORE = 0.92;
// « 12 beats maximum, not 12 seconds » (29/09/2026). Le nombre du panneau est
// un nombre de TEMPS, avec un indicateur de mesure : le compte part sur un
// premier temps, donc sur un nombre entier de mesures - 3 mesures, 12 temps,
// quel que soit le tempo (5,6 s à 128 BPM, 4,1 s à 174). Jamais moins de 8
// temps : la phase dramatique compte les 8 derniers. Les 10 s de tout visuel
// ne s'appliquent pas entre Countdown et son drop (espacerMarks les exempte
// l'un de l'autre, et le drop visé, fort, coupe toujours).
export const COUNTDOWN_MAX_BEATS = 12;
const COUNTDOWN_MIN_BEATS = 8;
const COUNTDOWN_ENERGY_AFTER_MS = 1500;

export function countdownTarget(marks, energyAt) {
  // LE DROP PRINCIPAL DÉCLARÉ (cue_points.main_drop_ms, 01/10/2026) passe
  // avant la mesure : sur la démonstration Party, l'oreille place le grand
  // passage du plus bas au plus haut à 1:04, quand le passage le plus FORT
  // est à 2:48 — l'énergie seule viserait le mauvais drop.
  const declare = (marks || []).find(function (m) { return m.type === 'drop' && m.principal; });
  if (declare) return { atMs: declare.atMs, score: Math.max(declare.score, COUNTDOWN_CERTAIN_SCORE), intensity: 1, declared: true };
  let best = null, bestI = -1;
  for (const m of marks || []) {
    if (m.type !== 'drop' || !(m.score >= COUNTDOWN_DROP_SCORE)) continue;
    const e = energyAt ? energyAt(m.atMs + COUNTDOWN_ENERGY_AFTER_MS) : null;
    const intensity = e != null ? e / 100 : m.score;
    if (!best || intensity > bestI + 1e-9 || (Math.abs(intensity - bestI) <= 1e-9 && m.score > best.score)) {
      best = m; bestI = intensity;
    }
  }
  return best ? { atMs: best.atMs, score: best.score, intensity: bestI } : null;
}

export function addCountdownMarks(marks, bpm, energyAt) {
  if (!marks || !marks.length || !bpm) return marks;
  const target = countdownTarget(marks, energyAt);
  if (!target || target.score < COUNTDOWN_CERTAIN_SCORE) return marks;
  const barMs = 4 * 60000 / bpm;
  let n = Math.floor(COUNTDOWN_MAX_BEATS / 4);
  let previousDrop = null;
  for (const m of marks) if (m.type === 'drop' && m.atMs < target.atMs) previousDrop = m.atMs;
  // Pas avant une mesure après le drop précédent — ni à moins de
  // MIN_VISUEL_MS de lui : espacerMarks donne la priorité au Countdown et
  // retirait alors ce drop, un point de mix voulu (Party : le drop de 0:49
  // disparaissait sous un compte de 12 temps vers 1:04 ; 8 temps le gardent).
  // Seulement quand ce drop peut survivre (au moins MIN_VISUEL_MS avant la
  // cible) et qu'il reste au moins COUNTDOWN_MIN_BEATS temps à compter.
  let earliest = Math.max(0, previousDrop != null ? previousDrop + barMs : 0);
  if (previousDrop != null && target.atMs - previousDrop >= MIN_VISUEL_MS
      && target.atMs - (previousDrop + MIN_VISUEL_MS) >= COUNTDOWN_MIN_BEATS * barMs / 4) {
    earliest = Math.max(earliest, previousDrop + MIN_VISUEL_MS);
  }
  while (n > 0 && target.atMs - n * barMs < earliest) n--;
  const start = target.atMs - n * barMs;
  if (n * 4 < COUNTDOWN_MIN_BEATS) return marks;
  const out = marks.slice();
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i].type !== 'drop' && out[i].atMs >= start - 300 && out[i].atMs < target.atMs) out.splice(i, 1);
  }
  out.push({ atMs: start, type: 'countdown', score: target.score, targetMs: target.atMs, bars: n });
  out.sort(function (a, b) { return a.atMs - b.atMs; });
  return out;
}

// UN VISUEL TIENT AU MOINS 10 SECONDES (29/09/2026, demande utilisateur).
//
// Chaque mark change la composition. Les points de phrase n'avaient aucun
// écart minimal entre eux : sur un titre à phrases de 8 temps, un visuel
// pouvait ne durer que 3 ou 4 secondes au milieu du morceau. On ne garde donc
// que des points espacés d'au moins MIN_VISUEL_MS, le début du morceau
// compris (le premier visuel part avec le titre), en servant d'abord les plus
// importants : Countdown et le drop qu'il vise, puis les drops du plus fort au
// plus faible, puis les autres points par score. Un point plus faible qui
// tombe trop près d'un point retenu est retiré - la ligne du panneau technique
// montre exactement les changements que l'image fera.
export const MIN_VISUEL_MS = 10000;

function prioriteMark(m, cibleCountdown) {
  if (m.type === 'countdown') return 3;
  if (cibleCountdown != null && m.type === 'drop' && m.atMs === cibleCountdown) return 3;
  if (m.type === 'drop') return 2;
  return 1;
}

export function espacerMarks(marks, minMs) {
  if (!marks || marks.length === 0) return marks;
  const ecart = minMs != null ? minMs : MIN_VISUEL_MS;
  const cd = marks.find(function (m) { return m.type === 'countdown'; });
  const cible = cd ? cd.targetMs : null;
  const ordre = marks.slice().sort(function (a, b) {
    const pa = prioriteMark(a, cible), pb = prioriteMark(b, cible);
    if (pa !== pb) return pb - pa;
    if (a.score !== b.score) return b.score - a.score;
    return a.atMs - b.atMs;
  });
  // Le début du morceau compte comme un changement déjà fait.
  const retenus = [];
  const instants = [0];
  for (const m of ordre) {
    // Countdown et son drop vont ensemble : leur écart est la durée du compte,
    // 8 à 12 temps (addCountdownMarks).
    const lie = prioriteMark(m, cible) === 3;
    const tropPres = instants.some(function (t) {
      if (lie && retenus.some(function (r) { return r.atMs === t && prioriteMark(r, cible) === 3; })) return false;
      return Math.abs(t - m.atMs) < ecart;
    });
    if (tropPres) continue;
    retenus.push(m);
    instants.push(m.atMs);
  }
  retenus.sort(function (a, b) { return a.atMs - b.atMs; });
  return retenus;
}

// --- Phase 1: precomputed timeline, O(1)-ish lookups by position ---

const BAND_BIN_MS = 100;
// Release time constant for the per-band envelope's exponential decay -
// attack is instant (a bin jumps straight to a higher raw value), release
// drifts back down over roughly this many ms once onsets stop.
const BAND_RELEASE_MS = 250;
const BAND_RELEASE_ALPHA = 1 - Math.exp(-BAND_BIN_MS / BAND_RELEASE_MS);
// Normalizing by the 95th percentile rather than the max - a single loud
// outlier onset shouldn't compress every other hit in the track toward 0.
const BAND_NORM_PERCENTILE = 0.95;
// A bin whose level rises by at least this much over the previous bin
// counts as a fresh peak (bandPeak snaps to 1); otherwise it decays.
const BAND_PEAK_RISE_THRESHOLD = 0.15;
const BAND_PEAK_DECAY_PER_BIN = 0.85;
const BAND_NAMES = ['low', 'mid', 'high'];

function binarySearchFloor(sortedAsc, value) {
  // Index of the largest element <= value (or 0 if value is before the
  // first element). Used for both the beat grid and the segment/band
  // lookups below - all of them are "which bucket does this ms fall into".
  let lo = 0, hi = sortedAsc.length - 1;
  if (value <= sortedAsc[0]) return 0;
  if (value >= sortedAsc[hi]) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (sortedAsc[mid] <= value) lo = mid; else hi = mid;
  }
  return lo;
}

function nearestBeatIndex(beatTimesMs, ms) {
  const lo = binarySearchFloor(beatTimesMs, ms);
  const hi = Math.min(beatTimesMs.length - 1, lo + 1);
  return (ms - beatTimesMs[lo] <= beatTimesMs[hi] - ms) ? lo : hi;
}

function percentile95(arr) {
  const positive = [];
  for (let i = 0; i < arr.length; i++) if (arr[i] > 0) positive.push(arr[i]);
  if (positive.length === 0) return 1;
  positive.sort(function (a, b) { return a - b; });
  return percentileOf(positive, BAND_NORM_PERCENTILE) || 1;
}

function segmentAtMs(segments, posMs) {
  for (let i = 0; i < segments.length; i++) {
    if (posMs >= segments[i].start_ms && posMs < segments[i].end_ms) return { seg: segments[i], index: i };
  }
  const lastIndex = segments.length - 1;
  return posMs >= segments[lastIndex].end_ms
    ? { seg: segments[lastIndex], index: lastIndex }
    : { seg: segments[0], index: 0 };
}

// Builds a fully precomputed, O(1)-lookup structure from one /analyze
// response - called once per track (see adaptive-party.js's
// trackAnalysisJustArrived), never recomputed per frame. Returns null when
// there isn't enough to build a real timeline from (no beat grid at all) -
// callers fall back to the legacy bpmFromAnalysis/energyFromAnalysis above
// plus the synthetic envelope, exactly as if no analysis existed.
// Les drops FORTS qui restent devant une position donnée, et dans combien de
// temps tombe le prochain. C'est la donnée dont la porte d'éligibilité de
// countdown.js a besoin, et elle est ici plutôt que dans le visualiseur parce
// que la sélection s'en sert AVANT qu'aucun visualiseur n'existe.
//
// Le seuil 0.6 est celui du brief. Il compte sur `score`, qui pour un mark de
// type 'drop' vaut la confiance du service (ancienne règle) ou 0.85 et plus
// (règle des phrases, moteur 1.1.0) - dans les deux cas une grandeur
// comparable, montante, et bornée à 1.
export const DROP_SCORE_MIN = 0.6;

export function dropsAhead(timeline, nowMs, minScore) {
  const seuil = minScore != null ? minScore : DROP_SCORE_MIN;
  if (!timeline || !timeline.marks) return { count: null, nextAtMs: null, nextMsUntil: null };
  let count = 0, nextAtMs = null;
  for (let i = 0; i < timeline.marks.length; i++) {
    const m = timeline.marks[i];
    if (m.type !== 'drop' || m.score < seuil) continue;
    if (m.atMs <= nowMs) continue;
    count++;
    if (nextAtMs == null) nextAtMs = m.atMs;
  }
  return { count: count, nextAtMs: nextAtMs, nextMsUntil: nextAtMs != null ? nextAtMs - nowMs : null };
}

// CE QUE LA PORTE D'ÉLIGIBILITÉ CONSOMME, dérivé d'un profil. Quatre champs,
// et rien d'autre : orchestrator.js n'a pas besoin de connaître la forme d'un
// profil, et les cinq endroits qui affichent ou parcourent le vivier n'ont pas
// besoin de refaire ce calcul chacun à sa façon.
export function porteDepuisProfil(profile) {
  const tl = profile ? profile.analysisTimeline : null;
  const nowMs = profile && (profile.estimatedPosition != null ? profile.estimatedPosition : profile.position) != null
    ? (profile.estimatedPosition != null ? profile.estimatedPosition : profile.position) * 1000
    : 0;
  const d = dropsAhead(tl, nowMs);
  const out = { hasAnalysis: !!tl, gridStrength: tl ? 1 : 0, dropsRemaining: d.count };
  if (d.nextMsUntil != null) out.msUntilNextDrop = d.nextMsUntil;
  return out;
}

export function buildAnalysisTimeline(analysis) {
  const beatTimesMs = analysis && analysis.tempo && analysis.tempo.beat_times_ms;
  if (!beatTimesMs || beatTimesMs.length < 2) return null;

  // --- 1a: real beat grid from beat_times_ms, not a constant-tempo
  // extrapolation - interpolates within the measured range, extrapolates
  // linearly off the nearest edge interval outside it. ---
  function beatCountAt(posMs) {
    const lastIdx = beatTimesMs.length - 1;
    if (posMs <= beatTimesMs[0]) {
      const span = beatTimesMs[1] - beatTimesMs[0];
      return span > 0 ? (posMs - beatTimesMs[0]) / span : 0;
    }
    if (posMs >= beatTimesMs[lastIdx]) {
      const span = beatTimesMs[lastIdx] - beatTimesMs[lastIdx - 1];
      return lastIdx + (span > 0 ? (posMs - beatTimesMs[lastIdx]) / span : 0);
    }
    const lo = binarySearchFloor(beatTimesMs, posMs);
    const hi = lo + 1;
    const span = beatTimesMs[hi] - beatTimesMs[lo];
    return lo + (span > 0 ? (posMs - beatTimesMs[lo]) / span : 0);
  }

  // --- 1b: real downbeat offset - the mode of (nearest beat index % 4)
  // across every segment start + mix_in_ms. Structural boundaries almost
  // always land on beat 1 of a bar, so this recovers which beat-index
  // parity IS beat 1, instead of assuming beat 0 is always a downbeat. ---
  // LES PHRASES D'ABORD (moteur 1.1.1) : leurs sections — une par phrase,
  // avec niveau et avg_db — remplacent les segments RMS pour le mode et
  // l'énergie continue. Alignées sur les points de mixage, sans le retard
  // du lissage ; les segments RMS restent le repli des analyses d'avant.
  const phrasesSections = analysis.structure && analysis.structure.phrases && Array.isArray(analysis.structure.phrases.sections)
    && analysis.structure.phrases.sections.length > 0 ? analysis.structure.phrases.sections : null;
  const segments = phrasesSections || (analysis.structure && analysis.structure.segments);
  const cue = analysis.structure && analysis.structure.cue_points;
  const boundaryMs = [];
  if (segments) for (let i = 0; i < segments.length; i++) boundaryMs.push(segments[i].start_ms);
  if (cue && cue.mix_in_ms != null) boundaryMs.push(cue.mix_in_ms);
  let barOffset = 0;
  if (boundaryMs.length > 0) {
    const counts = [0, 0, 0, 0];
    for (let i = 0; i < boundaryMs.length; i++) counts[nearestBeatIndex(beatTimesMs, boundaryMs[i]) % 4]++;
    for (let i = 1; i < 4; i++) if (counts[i] > counts[barOffset]) barOffset = i;
  }

  function lookupBeatPhase(posMs) {
    const beatCount = beatCountAt(posMs);
    const anchored = beatCount - barOffset;
    const beat = splitBeatCount(beatCount, 1);
    const bar = splitBeatCount(anchored, BEATS_PER_BAR);
    const phrase = splitBeatCount(anchored, BEATS_PER_PHRASE);
    const section = splitBeatCount(anchored, BEATS_PER_SECTION);
    // beatInBar uses the ANCHORED count (real downbeat offset applied),
    // not the raw beat index - so accent lines up with the real bar 1,
    // same reason barPhase itself is anchored.
    const beatInBar = ((Math.floor(anchored) % BEATS_PER_BAR) + BEATS_PER_BAR) % BEATS_PER_BAR;
    return {
      beatIndex: beat.index, beatPhase: beat.phase,
      barIndex: bar.index, barPhase: bar.phase,
      phraseIndex: phrase.index, phrasePhase: phrase.phase,
      sectionIndex: section.index, sectionPhase: section.phase,
      accent: ACCENTS[beatInBar]
    };
  }

  // --- 1c: per-band envelopes from onsets.events, one 100ms bin over the
  // whole track. ---
  const trackDurationMs = analysis.duration_s != null
    ? analysis.duration_s * 1000
    : beatTimesMs[beatTimesMs.length - 1] + 1000;
  const binCount = Math.max(1, Math.ceil(trackDurationMs / BAND_BIN_MS));
  const raw = { low: new Float32Array(binCount), mid: new Float32Array(binCount), high: new Float32Array(binCount) };
  const onsets = analysis.onsets && analysis.onsets.events;
  if (onsets) {
    for (let i = 0; i < onsets.length; i++) {
      const e = onsets[i];
      const bucket = raw[e.band];
      if (!bucket) continue; // unrecognized band label - ignore rather than guess which one it means
      const bin = clamp(Math.floor(e.time_ms / BAND_BIN_MS), 0, binCount - 1);
      bucket[bin] += e.strength || 0;
    }
  }

  const level = {};
  const bandPeak = {};
  for (let b = 0; b < BAND_NAMES.length; b++) {
    const name = BAND_NAMES[b];
    const src = raw[name];
    // Attack instant, release exponential - each bin either jumps up to a
    // higher raw onset sum or decays toward it from the previous bin.
    const smoothed = new Float32Array(binCount);
    let runningLevel = 0;
    for (let i = 0; i < binCount; i++) {
      runningLevel = src[i] > runningLevel ? src[i] : runningLevel + (src[i] - runningLevel) * BAND_RELEASE_ALPHA;
      smoothed[i] = runningLevel;
    }
    const norm = percentile95(smoothed);
    const normalized = new Float32Array(binCount);
    const peak = new Float32Array(binCount);
    let peakLevel = 0;
    for (let i = 0; i < binCount; i++) {
      normalized[i] = Math.min(1, smoothed[i] / norm);
      const rise = normalized[i] - (i > 0 ? normalized[i - 1] : 0);
      peakLevel = rise > BAND_PEAK_RISE_THRESHOLD ? 1 : peakLevel * BAND_PEAK_DECAY_PER_BIN;
      peak[i] = peakLevel;
    }
    level[name] = normalized;
    bandPeak[name] = peak;
  }

  function lookupBandEnvelope(posMs) {
    const bin = clamp(Math.floor(posMs / BAND_BIN_MS), 0, binCount - 1);
    return { low: level.low[bin], mid: level.mid[bin], high: level.high[bin] };
  }
  function lookupBandPeak(posMs) {
    const bin = clamp(Math.floor(posMs / BAND_BIN_MS), 0, binCount - 1);
    return { low: bandPeak.low[bin], mid: bandPeak.mid[bin], high: bandPeak.high[bin] };
  }

  // --- 1d: real structural mode + continuous energy from avg_db. ---
  const introEndMs = analysis.structure && analysis.structure.intro && analysis.structure.intro.end_ms;
  const outroStartMs = analysis.structure && analysis.structure.outro && analysis.structure.outro.start_ms;

  function lookupMode(posMs) {
    if (introEndMs != null && posMs < introEndMs) return 'intro';
    if (outroStartMs != null && posMs > outroStartMs) return 'outro';
    if (!segments || segments.length === 0) return 'sustain';
    const at = segmentAtMs(segments, posMs);
    if (at.seg.level === 'high') return 'drop';
    if (at.seg.level === 'low') {
      const prev = segments[at.index - 1], next = segments[at.index + 1];
      if (prev && next && prev.level === 'high' && next.level === 'high') return 'breakdown';
    }
    const next = segments[at.index + 1];
    if (next && next.level === 'high' && at.seg.level !== 'high') return 'build';
    return 'sustain';
  }

  let minDb = Infinity, maxDb = -Infinity;
  if (segments) {
    for (let i = 0; i < segments.length; i++) {
      if (segments[i].avg_db < minDb) minDb = segments[i].avg_db;
      if (segments[i].avg_db > maxDb) maxDb = segments[i].avg_db;
    }
  }
  function lookupEnergy(posMs) {
    if (!segments || segments.length === 0 || !isFinite(minDb) || !isFinite(maxDb) || maxDb === minDb) return null;
    const at = segmentAtMs(segments, posMs);
    return Math.round(clamp01((at.seg.avg_db - minDb) / (maxDb - minDb)) * 100);
  }

  // --- 1e: track "character" - dynamics and percussiveness, both static
  // per track (unlike everything above, not position-dependent). Used by
  // resonance.js to scale excitation, and by orchestrator.js's own
  // "prefers" scoring axis to match a visualizer's stated taste against
  // the actual track. null (not a guessed default) when the source field
  // is missing, same "absent means absent" rule as everywhere else here.
  const dynamicsDb = analysis.loudness && analysis.loudness.dynamic_range_db;
  // Heavily compressed masters (club/EDM, often mixed near -6 LUFS) sit
  // around 4-6dB of dynamic range; wide-dynamic-range acoustic/classical
  // material commonly runs 10-16dB. Normalized across that real spread,
  // not an arbitrary 0-1.
  // Syncopation - clave.js's own primary reactivity signal. That genre's
  // whole premise is rhythmic emphasis BETWEEN the beat, not on it, so this
  // measures exactly that: each onset's fractional position within its own
  // beat interval (via beatCountAt, already built above for lookupBeatPhase)
  // maps to 0 when it lands ON a beat and 1 when it lands EXACTLY between
  // two - then averaged across every onset, weighted by strength so a few
  // loud off-beat hits outweigh a scatter of quiet ones. A four-on-the-floor
  // house track lands close to 0; a son montuno or salsa clave sits well up
  // toward 1.
  let syncopation = null;
  if (onsets && onsets.length > 0) {
    let weightedSum = 0;
    let totalWeight = 0;
    for (let i = 0; i < onsets.length; i++) {
      const e = onsets[i];
      const beatFrac = beatCountAt(e.time_ms) % 1;
      const offBeat = beatFrac < 0.5 ? beatFrac * 2 : (1 - beatFrac) * 2;
      const weight = e.strength || 0;
      weightedSum += offBeat * weight;
      totalWeight += weight;
    }
    syncopation = totalWeight > 0 ? weightedSum / totalWeight : null;
  }

  const character = {
    dynamics: dynamicsDb != null ? clamp01((dynamicsDb - 4) / (14 - 4)) : null,
    // Onset density (events/second) as a stand-in for percussiveness - a
    // drum/EDM-heavy track packs far more onset events per second than a
    // legato acoustic/classical one. 8/s already reads as "very
    // percussive" in practice, so that's the top of the normalized range.
    percussiveness: (onsets && analysis.duration_s)
      ? clamp01((onsets.length / analysis.duration_s) / 8)
      : null,
    syncopation: syncopation
  };

  const choisis = selectMarks(analysis, analysis && analysis.mark_options);
  const principalMs = cue && cue.main_drop_ms;
  if (principalMs != null) {
    for (const m of choisis) if (m.type === 'drop' && Math.abs(m.atMs - principalMs) <= 500) m.principal = true;
  }
  const marks = espacerMarks(addCountdownMarks(choisis, bpmFromAnalysis(analysis), lookupEnergy));
  return {
    lookupBeatPhase: lookupBeatPhase,
    lookupBandEnvelope: lookupBandEnvelope,
    lookupBandPeak: lookupBandPeak,
    lookupMode: lookupMode,
    lookupEnergy: lookupEnergy,
    // Une analyse déclarée à la main (la démonstration) porte ses réglages :
    // ses points sont voulus un par un, le plafond « un toutes les 22 s » ne
    // doit pas en écarter.
    marks: marks,
    // the one drop Countdown may count to, or null - see countdownTarget
    countdownTarget: countdownTarget(choisis, lookupEnergy),
    character: character,
    // THE RAW DATA, for a visualizer that draws the whole track at once
    // (a height texture of every band bin, as Terrain did before it was removed): the measured
    // beat times, the structural segments (phrases first, RMS segments as
    // the fallback, as above), the per-band envelopes already binned at
    // BAND_BIN_MS and normalised, and the bin size. Read, never mutated.
    beatTimesMs: beatTimesMs,
    segments: segments || [],
    bands: level,
    bandBinMs: BAND_BIN_MS,
    binCount: binCount
  };
}
